import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormRecord } from '@angular/forms';
import { distinctUntilChanged, map } from 'rxjs';

import { FormBaseControlComponent } from '@plugins/client/forms/form';
import { Nullish } from '@plugins/shared/types';
import {
  customProfilePrefix,
  normalizedCustomProfilePrefix,
} from '@plugins/userdetails-lib/constants';

import { ProfileDataService } from '../services';

interface ProfileField {
  key: string;
  value: unknown;
}

@Component({
  selector: 'plugins-profile-field-picker',
  standalone: false,
  templateUrl: './profile-field-picker.component.html',
  styleUrl: './profile-field-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileFieldPickerComponent extends FormBaseControlComponent<
  string[]
> {
  readonly allowedFields = input<string[] | Nullish>(null);
  readonly requiredFields = input<Record<string, boolean>, string[]>(
    {},
    {
      transform: (fields) =>
        fields.reduce<Record<string, boolean>>((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {}),
    },
  );

  private readonly profileDataService = inject(ProfileDataService);

  form = new FormRecord<FormControl<boolean>>({});

  private readonly formDisabled = signal(false);

  private readonly formDisabledEffect = effect(() => {
    const formDisabled = this.formDisabled();
    Object.entries(this.form.controls).forEach(([key, control]) => {
      if (formDisabled) {
        control.disable({ emitEvent: false });
      } else {
        const isRequired = this.requiredFields()[this.parseField(key)];
        if (!isRequired) {
          control.enable({ emitEvent: false });
        }
      }
    });
  });

  private readonly requiredFieldsEffect = effect(() => {
    const requiredFields = this.requiredFields();
    let dirty = false;
    for (const [key, control] of Object.entries(this.form.controls)) {
      const isRequired = requiredFields[this.parseField(key)];
      if (isRequired) {
        if (!control.getRawValue()) {
          control.setValue(true, { emitEvent: false });
          dirty = true;
        }
        if (control.enabled) {
          control.disable({ emitEvent: false });
        }
      } else if (!this.formDisabled()) {
        control.enable({ emitEvent: false });
      }
    }
    if (dirty) {
      this.form.updateValueAndValidity();
    }
  });

  private readonly allProfileFields = toSignal(
    this.profileDataService
      .getProfileFull()
      .pipe(map((data) => this.extractProfileFields(data))),
  );

  private readonly formProfileFields = computed(() => {
    const allProfileFields = this.allProfileFields();
    if (!allProfileFields) {
      return;
    }

    // We can't guarantee that all users get same set of profile fields
    // so if a profile field was selected previously by ie editor we want to
    // keep and display that field for other users as well (if not use allowed to limit the fields)
    const selectedProfileFields = this.controlValueChange();
    if (selectedProfileFields) {
      for (let selectedField of selectedProfileFields) {
        selectedField = this.normalizeField(selectedField);
        const exists = allProfileFields.find(
          (field) => field.key === selectedField,
        );
        if (!exists) {
          allProfileFields.push({ key: selectedField, value: undefined });
        }
      }
    }
    // Same goes for allowed fields
    const allowedProfileFields = this.allowedFields()?.map(this.normalizeField);
    if (allowedProfileFields) {
      for (const allowedField of allowedProfileFields) {
        const exists = allProfileFields.find(
          (field) => field.key === allowedField,
        );
        if (!exists) {
          allProfileFields.push({ key: allowedField, value: undefined });
        }
      }
    }

    const formProfileFields = allowedProfileFields
      ? allProfileFields.filter((field) => {
          return allowedProfileFields.includes(field.key);
        })
      : allProfileFields;

    return formProfileFields;
  });

  private readonly formProfileFieldsEffect = effect(() => {
    const formProfileFields = this.formProfileFields();
    if (!formProfileFields) {
      return;
    }

    // Add new fields
    const currentFormProfileFields = Object.keys(this.form.controls);
    const newFields = formProfileFields.filter(
      (field) => !currentFormProfileFields.includes(field.key),
    );
    newFields.forEach((field) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
      const isRequired = !!this.requiredFields()[this.parseField(field.key)];
      this.addNewFormControl({
        controlName: field.key,
        value:
          isRequired ||
          !!this.control.getRawValue()?.includes(this.parseField(field.key)),
        disabled: isRequired || this.formDisabled(),
      });
    });
    // Remove fields
    const removedFields = currentFormProfileFields.filter((field) => {
      return !formProfileFields.find(({ key }) => key === field);
    });
    removedFields.forEach((field) => {
      this.form.removeControl(field, { emitEvent: false });
    });
    this.form.updateValueAndValidity();
  });

  readonly profileFields = computed(() => {
    const profileFields = this.formProfileFields();
    if (!profileFields) {
      return;
    }
    const requiredFields = this.requiredFields();
    // Sort required fields to the top
    profileFields.sort((a, b) => {
      const aRequired = requiredFields[a.key] ? 1 : 0;
      const bRequired = requiredFields[b.key] ? 1 : 0;
      return bRequired - aRequired;
    });
    return profileFields;
  });

  private readonly controlValueChange = toSignal(
    this.control.valueChanges.pipe(
      map(() => this.control.getRawValue()),
      distinctUntilChanged(
        (curr, prev) =>
          curr?.length === prev?.length &&
          !!curr?.every((v) => prev?.includes(v)),
      ),
    ),
    {
      initialValue: this.control.getRawValue(),
    },
  );

  private readonly formValueChange = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.getRawValue())),
  );

  private readonly controlValueChangeEffect = effect(() => {
    const value = this.controlValueChange();
    if (!value) {
      this.selectAll(false);
      return;
    }
    let dirty = false;
    // Unselect
    for (const [key, control] of Object.entries(this.form.controls)) {
      const field = this.parseField(key);
      if (
        control.getRawValue() &&
        !this.requiredFields()[field] &&
        !value.includes(field)
      ) {
        control.setValue(false, { emitEvent: false });
        dirty = true;
      }
    }
    // Select
    for (const field of value) {
      const controlName = this.normalizeField(field);
      const control = this.form.get(controlName);
      if (control && !control.getRawValue()) {
        control.setValue(true, { emitEvent: false });
        dirty = true;
      }
    }
    if (dirty) {
      this.form.updateValueAndValidity();
    }
  });

  private readonly formValueChangeEffect = effect(() => {
    const value = this.formValueChange();
    if (!value) {
      return;
    }
    const selectedFields = Object.entries(value).reduce<string[]>(
      (acc, [key, selected]) => {
        if (selected) {
          acc.push(this.parseField(key));
        }
        return acc;
      },
      [],
    );
    const controlValue = this.control.getRawValue();
    if (
      selectedFields.length === controlValue?.length &&
      selectedFields.every((field) => controlValue.includes(field))
    ) {
      return;
    }
    this.control.setValue(selectedFields);
  });

  readonly allSelected = computed(() => {
    const value = this.formValueChange();
    if (!value) {
      return false;
    }
    return Object.values(value).every((selected) => selected);
  });

  selectAll(select: boolean) {
    const requiredFields = this.requiredFields();
    let changed = false;
    for (const [key, control] of Object.entries(this.form.controls)) {
      if (!select && requiredFields[key]) {
        continue;
      }
      if (control.getRawValue() !== select) {
        control.setValue(select, { emitEvent: false });
        changed = true;
      }
    }
    if (changed) {
      this.form.updateValueAndValidity();
    }
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    this.formDisabled.set(isDisabled);
    this.cdRef.markForCheck();
  }

  private addNewFormControl({
    controlName,
    value,
    disabled,
  }: {
    controlName: string;
    value: boolean;
    disabled: boolean;
  }): void {
    const control = new FormControl(
      {
        value,
        disabled,
      },
      { nonNullable: true },
    );
    this.form.addControl(controlName, control, { emitEvent: false });
  }

  protected normalizeField(field: string): string {
    return field.replace(customProfilePrefix, normalizedCustomProfilePrefix);
  }

  protected parseField(field: string): string {
    return field.replace(normalizedCustomProfilePrefix, customProfilePrefix);
  }

  private extractProfileFields(data: Record<string, unknown>): ProfileField[] {
    return Object.entries(data).map(([key, value]) => {
      if (!key.startsWith(customProfilePrefix)) {
        return { key, value };
      }

      // control names with dots are not recognized in Angular forms
      const formControlName = this.normalizeField(key);

      return {
        key: formControlName,
        value,
      };
    });
  }
}
